import { useSelectBatchData, useSelectMethod, useSelectTheme, useSelectLoading } from "../../store";
import {
  AdherenceType,
  AdherenceViewedByType,
  Button,
  ButtonReverse,
  Input,
  ParticipantStatusFilter,
  type ParticipantStatusFilterOption,
  PARTICIPANT_STATUS_OPTIONS,
  ParticipantTagsFilter,
  Tooltip,
} from "..";
import { AdherenceViewTag, getTaskContentName } from "../../model";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useCallback,
  ChangeEvent,
  Dispatch,
  SetStateAction,
} from "react";
import {
  IGarminDevice,
  IMethod,
  IParticipant,
  IStopwatchGarminStream,
  ITag,
  ITask,
  ProjectCollaboratorRole,
  TaskType,
  WorkspaceCollaboratorRole,
} from "../../shared/db";
import {
  collaboratorTypeHasRole,
  useCreateTaskColorTranslator,
  RIF,
  sortBy,
  t,
  useCollaboratorTypeInProject,
  useCurrentProjectState,
  useSearch,
} from "../../lib";
import {
  AdherenceDigestDay,
  CellContext,
  CellContextType,
  CellSize,
  DigestDate,
  GridData,
  SelectedCellContextProvider,
} from "./adherence_digest_day";
import {
  AdherenceTableDateHeaderItem,
  AdherenceTableDateHeaderItemContext,
  AdherenceTableDateHeaderItemContextType,
} from "./adherence_date_header_item";
import { useInterval } from "react-use";
import { experimental_VGrid as VGrid, VGridHandle, VList, VListHandle } from "virtua";
import { Link, useNavigate, useParams } from "react-router-dom";
import React from "react";
import {
  LabfrontIcon,
  GarminConnectIcon,
  SearchIcon,
  DownloadGreyIcon,
  DownloadIcon,
} from "../../asset/image";
import {
  DateController,
  DateControllerRefType,
} from "../molecules/adherence_table_date_controller";
import _ from "lodash";
import { DateObject } from "react-multi-date-picker";
import { HeatMapLegend } from "./heat_map_legend";
import { TaskCompletionContentManageBar } from "./task_completion_content_manager_bar";
import AdherenceBackground from "../../asset/image/adherence_background.png";
import { AdherenceTableParticipantColumn } from "./adherence_table_participant_column";
import { ExportHeatmapDataPopup } from "../molecules/export_heatmap_data_popup";

export type ParticipantRealtimeDataMap = { [participantId: string]: string };

export interface GridIndex {
  participantIndex: number;
  digestDayIndex: number;
}

export const AdherenceTable = (props: {
  cellClicked: (participantId: string, yymmddIndex: number) => void;
  sendMessage: (participantList: IParticipant[]) => void;
  selectedList: IParticipant[];
  setSelectedList: Dispatch<SetStateAction<IParticipant[]>>;
}) => {
  // console.log('render AdherenceTable') // test code

  const { cellClicked, sendMessage, selectedList, setSelectedList } = props;

  const navigate = useNavigate();
  const { color, fontWeight, fontSize } = useSelectTheme();
  const colorTranslator = useCreateTaskColorTranslator();
  const { adherenceType } = useParams();
  const collaboratorType = useCollaboratorTypeInProject();
  const canUpdateParticipant = collaboratorTypeHasRole({
    collaboratorType,
    workspaceRole: WorkspaceCollaboratorRole.ParticipantUpdate,
    projectRole: ProjectCollaboratorRole.ParticipantUpdate,
  });

  // project data
  const { projectId, project } = useCurrentProjectState();
  const isScheduleProject = !!project?.scheduleConfig;
  const batchId = project?.batchList?.[0]?.id;
  const method = useSelectMethod();
  const batchData = useSelectBatchData();
  const participantList = useMemo(() => {
    if (batchId) return batchData?.[batchId]?.participantList ?? [];
    else return [];
  }, [batchData, batchId]);

  const notificationList = useMemo(
    () => selectedList.filter((p) => _.find(participantList, ["id", p.id])?.activated),
    [selectedList, participantList],
  );

  const fullTagList = useMemo(() => {
    if (!participantList.length) return [];
    const tagList: ITag[] = _.uniqBy(
      participantList.flatMap((p) => p.tagList),
      "id",
    ).filter((tag) => tag?.id) as ITag[];

    return tagList;
  }, [participantList]);

  useEffect(() => {
    setSelectedTagList(fullTagList);
  }, [fullTagList]);

  const garminConnectEnabled: boolean = method?.garminConnectEnable;
  const garminDirectEnabled = useMemo<boolean | undefined>(() => {
    if (!method?.taskList) return;
    const garminDeviceTask = _.find(method?.taskList, { type: TaskType.GarminDevice });
    return (
      (garminDeviceTask &&
        Object.values(garminDeviceTask.garminDevice as IGarminDevice).some((c) => c)) ??
      false
    );
  }, [method?.taskList]);
  const garminDirectBbiEnabled = useMemo<boolean | undefined>(() => {
    if (!method?.taskList) return;
    const garminDeviceTask = _.find(method?.taskList, { type: TaskType.GarminDevice });
    if (!garminDeviceTask?.garminDevice) return false;
    return !!(
      garminDeviceTask.garminDevice.bbiEnable || garminDeviceTask.garminDevice.enhancedBbiEnable
    );
  }, [method?.taskList]);
  const garminStreamTaskConfig = useMemo<IStopwatchGarminStream | undefined>(() => {
    return _.find(method?.taskList, {
      type: TaskType.StopwatchGarminStream,
    })?.stopwatchGarminStream;
  }, [method?.taskList]);

  const displayRealtimeData = useMemo(() => {
    return garminStreamTaskConfig?.realtimeUpload;
  }, [garminStreamTaskConfig]);

  // initialize dateList and digestList
  const dateList = useMemo(() => {
    if (!project?.createdAt) return [];
    const temp = [];

    const currentYYmmdd = new DateObject(new Date()).format("YYMMDD");
    const iterateDate = new Date(project?.createdAt);
    let yymmdd = new DateObject(iterateDate).format("YYMMDD");
    while (yymmdd <= currentYYmmdd) {
      temp.push(+yymmdd);
      iterateDate.setDate(iterateDate.getDate() + 1);
      yymmdd = new DateObject(iterateDate).format("YYMMDD");
    }
    return temp;
  }, [project]);

  const digestList = useMemo<DigestDate[]>(() => {
    return dateList.map((d) => {
      const [dayDate, dayUnixTimestamp] = t.yymmddToISOStringAndTimestamp(d);
      return {
        day: dayDate.slice(0, 10),
        dayDate,
        dayUnixTimestamp,
        yymmddIndex: d,
      };
    });
  }, [dateList]);

  // tag filter state
  const [untagged, setUntagged] = useState<boolean>(true);
  const [selectedTagList, setSelectedTagList] = useState<ITag[]>(fullTagList);

  const [selectedStatusList, setSelectedStatusList] = useState<ParticipantStatusFilterOption[]>([
    ...PARTICIPANT_STATUS_OPTIONS,
  ]);

  // displayParticipants
  const [displayParticipants, setDisplayParticipants] = useState<IParticipant[]>(participantList);
  const {
    searchValue,
    searchResult: searchedParticipants,
    setSearchValue,
  } = useSearch("insignia", participantList);

  const methodLoadReady = useMemo(() => {
    return garminDirectEnabled != undefined && garminConnectEnabled != undefined;
  }, [garminDirectEnabled, garminConnectEnabled]);

  // redirect away from BBI heatmap views when BBI is not enabled on the project
  useEffect(() => {
    if (garminDirectBbiEnabled !== false || !projectId) return;
    if (
      adherenceType === AdherenceType.GarminDirectBbiDigest ||
      adherenceType === AdherenceType.GCLFCombined
    ) {
      navigate(`/adherence/${projectId}/${AdherenceType.Overview}`);
    }
  }, [garminDirectBbiEnabled, adherenceType, projectId, navigate]);

  // filter displaying participants with tags
  useEffect(() => {
    const matchParticipantStatus = (
      participant: IParticipant,
      status: ParticipantStatusFilterOption,
    ) => {
      if (status === "notJoined") return !participant.activated;
      if (!participant.activated) return false;
      if (status === "completed") return participant.completedDate != null;
      return participant.completedDate == null;
    };

    let baseParticipants: IParticipant[] = [];

    if (fullTagList.length === 0 || (selectedTagList.length === fullTagList.length && untagged)) {
      baseParticipants = searchedParticipants;
    } else {
      const participantListFilteredByTag = participantList.filter((participant) => {
        if (untagged) {
          return !participant.tagList || participant.tagList.length === 0;
        }
        return selectedTagList.some(
          (tag) =>
            participant.tagList &&
            participant.tagList.length > 0 &&
            participant.tagList.some((t: ITag) => t.id === tag.id),
        );
      });

      baseParticipants = participantListFilteredByTag.reverse();
    }

    if (selectedStatusList.length === PARTICIPANT_STATUS_OPTIONS.length) {
      return setDisplayParticipants(baseParticipants);
    }
    if (selectedStatusList.length === 0) return setDisplayParticipants([]);

    return setDisplayParticipants(
      baseParticipants.filter((participant) =>
        selectedStatusList.some((status) => matchParticipantStatus(participant, status)),
      ),
    );
  }, [
    participantList,
    searchedParticipants,
    selectedTagList,
    selectedStatusList,
    fullTagList.length,
    untagged,
  ]);

  // viewTagList default setting
  function getDefaultViewTagList(method: IMethod) {
    const tempViewTagList: AdherenceViewTag[] = [];

    const garminConnectEnabled: boolean = method?.garminConnectEnable;
    const garminDeviceTask: ITask | undefined = _.find(method?.taskList, {
      type: TaskType.GarminDevice,
    });
    const dexcomIntegrationId = method?.dexcomIntegrationId;
    const taskList: ITask[] =
      method?.taskList?.filter((item) => item.type !== TaskType.GarminDevice && item.enabled) ?? [];
    const garminDirectEnabled =
      (garminDeviceTask &&
        Object.values(garminDeviceTask.garminDevice as IGarminDevice).some((c) => c)) ??
      false;

    if (garminConnectEnabled)
      tempViewTagList.push({
        name: "Garmin Connect App",
        color: color.black,
        alt: "Garmin Connect data",
      });
    if (garminDirectEnabled) {
      tempViewTagList.push({
        name: "Labfront Companion App",
        color: color.primary,
        id: garminDeviceTask?.id,
        alt: "Labfront data",
      });
    }
    if (dexcomIntegrationId) {
      tempViewTagList.push({
        name: "Dexcom App",
        color: color.dexcomGreen,
        id: dexcomIntegrationId,
        alt: "Dexcom data",
      });
    }
    if (taskList?.length) {
      tempViewTagList.push(
        ...taskList
          .filter((task) => task.type)
          .sort(sortBy("index"))
          .map((item, index) => ({
            id: item.id,
            name: item[getTaskContentName(item.type)]?.name ?? "",
            color: item.color ? `#${item.color}` : colorTranslator(index),
            index: item.index ?? index + 1,
            alt: `Task ${index + 1}`,
          })),
      );
    }
    return tempViewTagList;
  }

  const [adherenceViewedByType, setAdherenceViewedByType] = useState<AdherenceViewedByType>(
    AdherenceViewedByType.Tasks,
  );
  const [viewTagList, setViewTagList] = useState<AdherenceViewTag[]>([]);

  useEffect(() => {
    setViewTagList(getDefaultViewTagList(method));
  }, [method]);

  const handleClickCell = (participantId: string, yymmddIndex: number) => {
    cellClicked(participantId, yymmddIndex);
  };
  // state that are used to determine whether to show the box shadow of the scrollbar
  const [showLeftBoxShadowOfScrollbar, setShowLeftBoxShadowOfScrollbar] = useState(true);
  const [showRightBoxShadowOfScrollbar, setShowRightBoxShadowOfScrollbar] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const digestGridRef = useRef<VGridHandle>(null);
  const dateHeaderListRef = useRef<VListHandle>(null);
  const participantHeaderListRef = useRef<VListHandle>(null);

  // calculate the width of the column based on the number of elements in a row
  // and the height of the row based on the number of elements in a column
  const MAX_ELEMENTS_IN_ROW = 7;
  const PADDING_VERTICAL = 32;
  const PADDING_HORIZONTAL = 48;
  const ELEMENT_HEIGHT = 13;
  const ELEMENT_WIDTH = 39;
  const ELEMENT_GAP = 16;
  const ELEMENT_EXPAND_WIDTH = 32;
  const WEAR_TIME_HEATMAP_CELL_WIDTH = 40;
  const WEAR_TIME_HEATMAP_CELL_HEIGHT = 48;
  const DEFAULT_COLUMN_WIDTH = 210;

  const isOverview = useMemo(() => {
    return adherenceType == AdherenceType.Overview;
  }, [adherenceType]);

  const rowCount = useMemo(() => {
    if (isOverview) {
      return Math.ceil(viewTagList.length / MAX_ELEMENTS_IN_ROW);
    } else {
      return 1;
    }
  }, [viewTagList, isOverview]);

  const rowHeight = useMemo(() => {
    if (isOverview) {
      return Math.max(
        48,
        PADDING_VERTICAL + ELEMENT_HEIGHT * rowCount + ELEMENT_GAP * (rowCount - 1),
      );
    } else {
      return WEAR_TIME_HEATMAP_CELL_HEIGHT;
    }
  }, [rowCount, isOverview]);

  const digestDayColumnWidth = useMemo(() => {
    if (!isOverview) return WEAR_TIME_HEATMAP_CELL_WIDTH;
    if (!viewTagList.length) return DEFAULT_COLUMN_WIDTH;
    const dotWidthList = viewTagList.map((v, i) => {
      if (["Garmin Connect App", "Labfront Companion App"].includes(v.name))
        return i > 0 ? ELEMENT_WIDTH + ELEMENT_GAP : ELEMENT_WIDTH;
      return i > 0 ? ELEMENT_EXPAND_WIDTH + ELEMENT_GAP : ELEMENT_EXPAND_WIDTH;
    });
    const result = Math.max(
      ...Array(rowCount)
        .fill(0)
        .map(
          (_, i) =>
            dotWidthList
              .slice(i * MAX_ELEMENTS_IN_ROW, (i + 1) * MAX_ELEMENTS_IN_ROW)
              .reduce((a, b) => a + b, 0) + PADDING_HORIZONTAL,
        ),
      DEFAULT_COLUMN_WIDTH,
    );
    return result;
  }, [isOverview, viewTagList]);

  const [availableWidth, setAvailableWidth] = useState(digestDayColumnWidth * 3);

  // Overlay shadows sit above opaque cell backgrounds so every row shows the scroll hint.
  const scrollShadowOverlays = useMemo(
    () => (
      <>
        {RIF(
          showLeftBoxShadowOfScrollbar,
          <div
            css={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "10px",
              pointerEvents: "none",
              zIndex: 2,
              boxShadow: "inset 10px 0 10px -5px rgba(0, 0, 0, 0.1)",
            }}
          />,
        )}
        {RIF(
          showRightBoxShadowOfScrollbar,
          <div
            css={{
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: "10px",
              pointerEvents: "none",
              zIndex: 2,
              boxShadow: "inset -10px 0 10px -5px rgba(0, 0, 0, 0.1)",
            }}
          />,
        )}
      </>
    ),
    [showLeftBoxShadowOfScrollbar, showRightBoxShadowOfScrollbar],
  );

  // Calculate available width for the scrollable part
  useLayoutEffect(() => {
    const calculateAvailableWidth = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.clientWidth;
      const fixedPartWidth = (displayRealtimeData ? 600 : 495) + (canUpdateParticipant ? 32 : 0);

      setAvailableWidth(containerWidth - fixedPartWidth);
    };

    calculateAvailableWidth();
    // Update width on window resize
    window.addEventListener("resize", calculateAvailableWidth);
    return () => window.removeEventListener("resize", calculateAvailableWidth);
  }, [containerRef.current, displayRealtimeData, digestDayColumnWidth, canUpdateParticipant]);

  const datePickerIndexRef = useRef(dateList.length ? dateList.length - 1 : 0);
  const dateControllerRef = useRef<DateControllerRefType>(null);

  const participantListRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const participantElement = participantListRef.current;
    const preventScroll = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleMouseDown = (e: MouseEvent) => {
      // Only prevent middle button (button 1)
      if (e.button === 1) {
        e.preventDefault();
        e.stopPropagation();

        // Also prevent the subsequent mousemove events while middle button is held
        const handleMouseMove = (moveEvent: MouseEvent) => {
          moveEvent.preventDefault();
          moveEvent.stopPropagation();
        };

        const handleMouseUp = () => {
          participantElement?.removeEventListener("mousemove", handleMouseMove);
          participantElement?.removeEventListener("mouseup", handleMouseUp);
        };

        participantElement?.addEventListener("mousemove", handleMouseMove, { passive: false });
        participantElement?.addEventListener("mouseup", handleMouseUp, { once: true });
      }
    };

    if (participantElement) {
      participantElement.addEventListener("wheel", preventScroll, { passive: false });
      participantElement.addEventListener("touchmove", preventScroll, { passive: false });
      participantElement.addEventListener("dragstart", preventScroll, { passive: false });
      participantElement.addEventListener("mousedown", handleMouseDown, { passive: false });
    }

    return () => {
      if (participantElement) {
        participantElement.removeEventListener("wheel", preventScroll);
        participantElement.removeEventListener("touchmove", preventScroll);
        participantElement.removeEventListener("dragstart", preventScroll);
        participantElement.removeEventListener("mousedown", handleMouseDown);
      }
    };
  }, []);

  useEffect(() => {
    if (dateList.length) {
      datePickerIndexRef.current = dateList.length - 1;
    }
  }, [dateList]);

  const [highLightBox, setHighLightBox] = useState("");

  // useEffect(() => {
  //   if (!displaySidebar) {
  //     setHighLightBox('')
  //   }
  // }, [displaySidebar])

  const DateHeaderCell = (props: { index: number; datePickerIndex: number }) => {
    const { index, datePickerIndex } = props;
    return (
      <AdherenceTableDateHeaderItem
        {...{
          index,
          datePickerIndex,
        }}
      />
    );
  };

  const AdherenceTypeSelections = () => {
    enum AdherenceTypeButtonName {
      Overview = "Overview",
      GarminConnectWearTime = "Heart Rate Heatmap",
      GarminDirectBbiDigest = "BBI Heatmap",
      GCLFCombined = "Heart Rate/BBI Heatmap",
    }

    const SelectAdherenceTypeButton = (props: {
      type: AdherenceType;
      buttonName: AdherenceTypeButtonName;
    }) => {
      const { type, buttonName } = props;
      const selected = adherenceType === type;
      return (
        <button
          onClick={() => {
            if (adherenceType !== type) {
              navigate(`/adherence/${projectId}/${type}`);
            }
          }}
          css={{
            padding: "8px 12px",
            boxSizing: "border-box",
            backgroundColor: selected ? color.surface.blue.light : "transparent",
            border: selected ? `1px solid ${color.border.blueDark}` : "none",
            borderRadius: "999px",
            cursor: "pointer",
            display: "flex",
            flexWrap: "nowrap",
            alignItems: "center",
            gap: 8,
            ":hover": {
              backgroundColor: selected ? color.surface.blue.light : color.surface.grey.light,
            },
          }}
        >
          {buttonName !== AdherenceTypeButtonName.Overview && (
            <div
              css={{
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              {[
                AdherenceTypeButtonName.GarminDirectBbiDigest,
                AdherenceTypeButtonName.GCLFCombined,
              ].includes(buttonName) && <img width={16} height={16} src={LabfrontIcon} />}
              {[
                AdherenceTypeButtonName.GarminConnectWearTime,
                AdherenceTypeButtonName.GCLFCombined,
              ].includes(buttonName) && <img width={16} height={16} src={GarminConnectIcon} />}
            </div>
          )}
          <span
            css={{
              fontWeight: selected ? fontWeight.thick : fontWeight.medium,
              color: selected ? color.primary : color.textIcon.secondary,
              flex: 1,
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {buttonName}
          </span>
        </button>
      );
    };

    if (garminConnectEnabled || garminDirectBbiEnabled) {
      return (
        <div
          css={{
            height: 48,
            padding: 8,
            display: "flex",
            alignItems: "center",
            gap: 16,
            flexShrink: 0,
            maxWidth: "100%",
            marginRight: "auto",
            background: color.white,
            borderRadius: "999px",
            boxShadow: "0px 4px 12px 0px #D4D4D440",
          }}
        >
          <SelectAdherenceTypeButton
            {...{
              type: AdherenceType.Overview,
              buttonName: AdherenceTypeButtonName.Overview,
            }}
          />
          {(garminConnectEnabled || garminDirectBbiEnabled) && (
            <div
              css={{
                height: "100%",
                width: 0,
                borderRight: `1px solid ${color.border.default}`,
              }}
            />
          )}
          {garminConnectEnabled && (
            <SelectAdherenceTypeButton
              {...{
                type: AdherenceType.GarminConnectWearTime,
                buttonName: AdherenceTypeButtonName.GarminConnectWearTime,
              }}
            />
          )}
          {garminDirectBbiEnabled && (
            <SelectAdherenceTypeButton
              {...{
                type: AdherenceType.GarminDirectBbiDigest,
                buttonName: AdherenceTypeButtonName.GarminDirectBbiDigest,
              }}
            />
          )}
          {garminConnectEnabled && garminDirectBbiEnabled && (
            <SelectAdherenceTypeButton
              {...{
                type: AdherenceType.GCLFCombined,
                buttonName: AdherenceTypeButtonName.GCLFCombined,
              }}
            />
          )}
        </div>
      );
    } else {
      return <div></div>;
    }
  };

  const TableController = () => {
    return (
      <div
        css={{
          width: "100%",
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "16px",
          flexWrap: "wrap",
          position: "sticky",
          top: "48px",
          backgroundColor: color.background,
          zIndex: "99",
        }}
      >
        <AdherenceTypeSelections />

        <div
          css={{
            position: "relative",
            width: "180px",
            height: "40px",
            flexShrink: 0,
            border: `1px solid ${color.grey_160}`,
            borderRadius: "5px",
            background: color.white,
            ":hover": {
              border: `1px solid ${color.grey_400}`,
              cursor: "pointer",
            },
          }}
        >
          <Input
            value={searchValue}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
            placeholder="Search Participants"
            style={{
              border: "none",
              background: "inherit",
              height: "100%",
              width: "100%",
              padding: "8px 32px 8px 12px",
            }}
          />
          <img
            alt="Magnifying glass"
            src={SearchIcon}
            width="16"
            height="16"
            css={{
              position: "absolute",
              top: "12px",
              right: "8px",
            }}
          />
        </div>
        {RIF(
          fullTagList.length > 0,
          <ParticipantTagsFilter
            size={"large"}
            {...{
              fullTagList,
              selectedTagList,
              setSelectedTagList,
              untagged,
              setUntagged,
            }}
          />,
        )}
        {RIF(
          participantList.length > 0,
          <ParticipantStatusFilter
            size={"large"}
            {...{
              selectedStatusList,
              setSelectedStatusList,
            }}
          />,
        )}
        <DateController
          ref={dateControllerRef}
          {...{
            project,
            initialDatePickerIndex: datePickerIndexRef.current,
            dateList,
            garminDirectBbiEnabled,
            garminConnectEnabled,
            datePickerIndexUpdated,
          }}
        />
      </div>
    );
  };

  const OverViewTableTaskFilterBar = () => {
    if (adherenceType === AdherenceType.Overview) {
      return (
        <TaskCompletionContentManageBar
          {...{
            viewTagList,
            setViewTagList,
            adherenceViewedByType,
            setAdherenceViewedByType,
            garminConnectEnabled,
            garminDirectEnabled,
          }}
        />
      );
    }
    return <></>;
  };

  //probably cause by some kind of internal optimization by the library, item
  //will have a different offset than the theory calculation result, can use the
  //first visible item offset to compare with theory value to get the difference
  //, the difference can be used when scrolling item programmatically to adjust
  //the offset to the idea position.
  const getGridOffsetDiff: (gridHandle: VGridHandle) => number = (gridHandle) => {
    const startIndex = gridHandle.findStartIndex()[0];
    const expectedStartOffset = digestDayColumnWidth * startIndex;
    const actualStartOffset = gridHandle.getItemOffset(startIndex, 0)[0];
    return expectedStartOffset - actualStartOffset;
  };

  const dateHeaderContext = useMemo<AdherenceTableDateHeaderItemContextType>(() => {
    return {
      adherenceType,
      digestDayColumnWidth,
      showRightBorder: false,
      digestList,
    };
  }, [adherenceType, digestDayColumnWidth, digestList]);

  const AdherenceTableDateHeaderItemContextProvider = React.memo(
    function contextProvider({
      dateHeaderContext,
      children,
    }: {
      dateHeaderContext: AdherenceTableDateHeaderItemContextType;
      children: React.ReactNode;
    }) {
      return (
        <AdherenceTableDateHeaderItemContext.Provider value={{ ...dateHeaderContext }}>
          {children}
        </AdherenceTableDateHeaderItemContext.Provider>
      );
    },
    (prev, next) => {
      return prev.dateHeaderContext === next.dateHeaderContext;
    },
  );

  const AdherenceDateRow = useMemo(() => {
    if (!dateHeaderContext) {
      return <></>;
    }

    const { digestList, digestDayColumnWidth } = dateHeaderContext;
    return (
      <AdherenceTableDateHeaderItemContextProvider dateHeaderContext={dateHeaderContext}>
        <VList
          style={{
            minHeight: 56,
            height: 56,
            width: availableWidth,
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            pointerEvents: "none",
            borderTopRightRadius: 5,
          }}
          count={digestList.length}
          itemSize={digestDayColumnWidth}
          horizontal={true}
          ref={dateHeaderListRef}
        >
          {(index) => <DateHeaderCell index={index} datePickerIndex={datePickerIndexRef.current} />}
        </VList>
      </AdherenceTableDateHeaderItemContextProvider>
    );
  }, [dateHeaderContext, availableWidth]);

  const CellContextProvider = React.memo(
    function cellContextProvider({
      cellContext,
      children,
    }: {
      cellContext: CellContextType;
      children: React.ReactNode;
    }) {
      return <CellContext.Provider value={{ ...cellContext }}>{children}</CellContext.Provider>;
    },
    (prev, next) => {
      return prev.cellContext === next.cellContext;
    },
  );

  const gridData = useMemo<GridData | undefined>(() => {
    if (digestList.length && displayParticipants.length) {
      return {
        digestDateList: digestList,
        participantList: displayParticipants,
      };
    }
    return;
  }, [displayParticipants, digestList]);

  const cellSize = useMemo<CellSize>(() => {
    return {
      width: digestDayColumnWidth,
      height: rowHeight,
    };
  }, [rowHeight, digestDayColumnWidth]);

  const cellContext = useMemo<CellContextType | undefined>(() => {
    if (methodLoadReady && adherenceType && gridData) {
      return {
        isScheduleProject,
        adherenceType,
        gridData,
        cellSize,
        viewTagList,
      };
    }
    return;
  }, [methodLoadReady, isScheduleProject, adherenceType, gridData, cellSize, viewTagList]);

  const renderCell = useCallback(
    ({ rowIndex, colIndex }: { rowIndex: number; colIndex: number }) => {
      return <MemoDigestCell {...{ participantIndex: rowIndex, digestDateIndex: colIndex }} />;
    },
    [],
  );

  const MemoDigestCell = React.memo(
    ({
      participantIndex,
      digestDateIndex,
    }: {
      participantIndex: number;
      digestDateIndex: number;
    }) => {
      return DigestCell({ participantIndex, digestDateIndex });
    },
    (prev, next) => {
      return (
        prev.participantIndex == next.participantIndex &&
        prev.digestDateIndex == next.digestDateIndex
      );
    },
  );

  const DigestCell = (props: { participantIndex: number; digestDateIndex: number }) => {
    const { participantIndex, digestDateIndex } = props;
    return (
      <AdherenceDigestDay
        {...{
          participantIndex,
          digestDateIndex,
          projectId,
          adherenceViewedByType,
          highLightBox,
          setHighLightBox,
          handleClickCell,
        }}
      />
    );
  };

  const gridScrollEnd = () => {
    actionOnScrollEnd();
  };

  const AdherenceCellTable = useMemo(() => {
    console.log("AdherenceCellTable mounted");
    if (!project || !cellContext) {
      return <></>;
    }

    const { gridData, cellSize } = cellContext;
    const rowLength = gridData.participantList.length;
    const colLength = gridData.digestDateList.length;

    return (
      <>
        <CellContextProvider cellContext={cellContext}>
          <SelectedCellContextProvider>
            <VGrid
              style={{
                height: cellSize.height * 20,
                width: availableWidth,
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
              row={rowLength}
              col={colLength}
              cellHeight={cellSize.height}
              key={projectId}
              cellWidth={cellSize.width}
              // onScroll={digestGridOnScroll}
              onScrollEnd={gridScrollEnd}
              ref={digestGridRef}
            >
              {renderCell}
            </VGrid>
          </SelectedCellContextProvider>
        </CellContextProvider>
      </>
    );
  }, [cellContext, availableWidth]);

  const getFixedEndXIdex = () => {
    if (!digestGridRef.current) return;

    // VGrid seems to have bug when call onScrollEnd, the digestGridRef.current.findEndIndex() xIndex will add 1 out of viewing range
    // so make a workaround to fix the issue
    const endIndex = digestGridRef.current.findEndIndex();
    let xIndex = endIndex[0];
    if (xIndex != dateList.length - 1) {
      xIndex = xIndex - 1;
    }
    return xIndex;
  };

  const lastTopRightIndexRef = useRef<[number, number]>();
  const actionOnScrollEnd = () => {
    if (!digestGridRef.current) return;

    const startIndex = digestGridRef.current.findStartIndex();
    const xIndex = getFixedEndXIdex();
    if (xIndex) {
      lastTopRightIndexRef.current = [xIndex, startIndex[1]];
      updateDateControlViewRangeIndexes(startIndex[0], xIndex); // this determines the cellTable scroll position when adherence changed
    }
  };

  const scrollToDatePickerIndex = (datePickerIndex: number) => {
    if (!digestGridRef.current) return;

    const xIndex = getFixedEndXIdex();
    if (xIndex === datePickerIndex) {
      // same index skip scrolling
      return;
    }
    const itemSize = digestGridRef.current.getItemSize(0, 0);
    const cellWidth = itemSize[0];
    const cellHeight = itemSize[1];

    const totalWidth = cellWidth * digestList.length;
    const diff = 0; //getGridOffsetDiff(digestGridRef.current) // alawys zero?
    const offsetCellCount = dateList.length - datePickerIndex - 1;
    const targetOffsetFromRight = cellWidth * offsetCellCount;
    const xScrollOffset = totalWidth - targetOffsetFromRight - availableWidth - diff;
    const yScrollOffset = lastTopRightIndexRef.current
      ? lastTopRightIndexRef.current[1] * cellHeight
      : 0;
    digestGridRef.current?.scrollTo(xScrollOffset, yScrollOffset);
  };

  const updateDateControlViewRangeIndexes = (startIndex: number, endIndex: number) => {
    if (dateControllerRef.current) {
      dateControllerRef.current.updateViewRangeIndexes(startIndex, endIndex);
    }
  };

  const digestGridOnScroll = () => {
    // syncHeaderOffsetWithGrid()
    // setShowLeftBoxShadowOfScrollbar((digestGridRef.current?.scrollLeft ?? 1) > 0)
  };

  const AdherenceTable = () => {
    return (
      <div
        ref={containerRef}
        css={{
          display: "flex",
          background: color.white,
          borderRadius: 5,
          border: `1px solid ${color.grey_100}`,
          flex: 1,
          overflow: "hidden",
        }}
      >
        {/* fixed part of the table */}
        <AdherenceTableParticipantColumn
          {...{
            projectId,
            displayParticipants,
            garminStreamTaskConfig,
            displayRealtimeData,
            rowHeight,
            sendMessage,
            selectedList,
            setSelectedList,
            participantListRef,
            vListHandler: participantHeaderListRef,
          }}
        />

        {/* scrollable part of the table */}
        <div
          css={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            backgroundColor: color.surface.default,
            borderTopRightRadius: "5px",
          }}
        >
          {scrollShadowOverlays}
          {AdherenceDateRow}
          {AdherenceCellTable}
        </div>
      </div>
    );
  };

  // initial scroll to newest date when adherenceCellTable ready
  useEffect(() => {
    if (cellContext && digestGridRef.current) {
      digestGridRef.current.scrollToIndex(digestList.length, 0);
      dateHeaderListRef.current?.scrollToIndex(digestList.length);
    }
  }, [AdherenceCellTable]);

  const headerOffsetRef = useRef<[x: number, y: number]>([0, 0]);

  const updateCellTableTopLeftOffset = (): boolean => {
    const gridRef = digestGridRef.current;
    if (!gridRef) return false;
    if (
      gridRef.scrollLeft != headerOffsetRef.current[0] ||
      gridRef.scrollTop != headerOffsetRef.current[1]
    ) {
      headerOffsetRef.current = [gridRef.scrollLeft, gridRef.scrollTop];
      return true;
    }
    return false;
  };

  const syncHeaderOffsetWithGrid = () => {
    if (headerOffsetRef.current) {
      const dateRef = dateHeaderListRef.current;
      if (dateRef) {
        dateRef.scrollTo(headerOffsetRef.current[0]);
      }
      const participantRef = participantHeaderListRef.current;
      if (participantRef) {
        participantRef.scrollTo(headerOffsetRef.current[1]);
      }
    }
  };

  useInterval(() => {
    const offsetUpdated = updateCellTableTopLeftOffset();
    if (offsetUpdated) {
      syncHeaderOffsetWithGrid();
    }
  }, 200);

  const [showExportHeatmapDataPopup, setShowExportHeatmapDataPopup] = useState(false);

  // scroll to last position when adherenceType or viewTagList changed
  useEffect(() => {
    syncHeaderOffsetWithGrid();
    scrollToDatePickerIndex(datePickerIndexRef.current);
  }, [adherenceType, showExportHeatmapDataPopup]);

  // call back for dateController
  const datePickerIndexUpdated = useCallback(
    (datePickerIndex: number) => {
      datePickerIndexRef.current = datePickerIndex;
      scrollToDatePickerIndex(datePickerIndex);
    },
    [AdherenceCellTable, availableWidth],
  );

  const AdherenceTableContent = () => {
    return (
      <>
        {TableController()}
        <div
          css={{
            display: "flex",
            maxHeight: "100%",
            overflow: "hidden",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {OverViewTableTaskFilterBar()}
          {AdherenceTable()}
          <HeatMapLegend adherenceType={adherenceType} />
        </div>
      </>
    );
  };

  const AdherenceTitle = () => (
    <div
      css={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: "20px",
        minWidth: 0,
      }}
    >
      <div
        css={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <span
          css={{
            fontWeight: fontWeight.thick,
            fontSize: fontSize.h3,
          }}
        >
          Adherence
        </span>
        <span css={{ color: color.textIcon.secondary }}>— All times based on Participant time</span>
        <Tooltip content="Participant data adherence" />
      </div>
    </div>
  );

  const openExportHeatmapdataPopupButton = () => {
    if (isOverview) return;
    else {
      return (
        <ButtonReverse
          onClick={() => setShowExportHeatmapDataPopup(true)}
          btnPadding="medium"
          btnColor="primary"
          css={{ position: "relative" }}
          children={
            <div
              css={{
                display: "flex",
                alignItems: "center",
                color: color.primary,
                fontWeight: fontWeight.bold,
              }}
            >
              Export Heatmap Data
            </div>
          }
        />
      );
    }
  };

  const onClickCancel = () => {
    setShowExportHeatmapDataPopup(false);
  };

  const HeaderSection = () => (
    <div
      css={{
        width: "100%",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "16px",
      }}
    >
      {AdherenceTitle()}
      <div css={{ display: "flex", alignItems: "center", gap: "16px", flexShrink: 0 }}>
        {RIF(
          canUpdateParticipant && selectedList.length > 0,
          <div css={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span>{selectedList.length} Selected</span>
            <ButtonReverse
              onClick={() => sendMessage(selectedList)}
              disabled={notificationList.length === 0}
              btnPadding="medium"
            >
              <span
                css={{
                  color: notificationList.length === 0 ? color.textIcon.disabled : color.primary,
                }}
              >
                Send message to active participants ({notificationList.length})
              </span>
            </ButtonReverse>
          </div>,
        )}
        {openExportHeatmapdataPopupButton()}
      </div>
    </div>
  );

  const AdherenceTableBlock = () => {
    if (!project) return <></>;

    if (participantList.length) {
      return (
        <>
          {HeaderSection()}
          {AdherenceTableContent()}
          {RIF(
            showExportHeatmapDataPopup,
            <ExportHeatmapDataPopup
              {...{
                garminConnectEnabled,
                garminDirectBbiEnabled,
                onClickCancel,
              }}
            />,
          )}
        </>
      );
    } else {
      return (
        <>
          {HeaderSection()}
          <AdherenceNoParticipantContent />
        </>
      );
    }
  };

  return <>{AdherenceTableBlock()}</>;
};

const AdherenceNoParticipantContent = () => {
  const loadingState = useSelectLoading();
  const { color, fontWeight, fontSize } = useSelectTheme();
  const { projectId } = useCurrentProjectState();

  if (_.isEmpty(loadingState)) {
    return (
      <div
        css={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          flex: 1,
          border: `1px dashed ${color.border.dark}`,
          borderRadius: "5px",
        }}
      >
        <img alt="" src={AdherenceBackground} width="240" />
        <div
          css={{
            color: color.grey_400,
            fontWeight: fontWeight.medium,
            fontSize: fontSize.h5,
            width: "52%",
            margin: "40px auto 20px",
            textAlign: "center",
          }}
        >
          With the adherence dashboard, you can view participants' collected data and completed
          tasks in real-time. Save yourself from missing data before it's too late.
        </div>
        <Link to={`/participants/${projectId}`}>
          <Button>Add participants to start</Button>
        </Link>
      </div>
    );
  }
  return <></>;
};
